import { useEffect, useState, useRef } from 'react'
import { LOW_RES_MARKER_ENABLED } from './constants'

export interface LowResImage {
  src: string
  naturalWidth: number
  naturalHeight: number
  displayWidth: number
  displayHeight: number
  dpi: number
}

const MARKER_CLASS = 'low-res-marker'
const MARKER_STYLE_ID = 'low-res-marker-style'

function ensureMarkerStyles() {
  if (document.getElementById(MARKER_STYLE_ID)) return
  const style = document.createElement('style')
  style.id = MARKER_STYLE_ID
  style.textContent = `
    .${MARKER_CLASS}-badge {
      position: absolute;
      top: 2px;
      right: 2px;
      background: #ef4444;
      color: white;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
      padding: 2px 5px;
      border-radius: 3px;
      pointer-events: none;
      z-index: 5;
    }
    @media print {
      .${MARKER_CLASS}-badge { display: none !important; }
    }
  `
  document.head.appendChild(style)
}

function clearMarkers(container: HTMLElement) {
  container.querySelectorAll(`.${MARKER_CLASS}`).forEach(el => {
    el.classList.remove(MARKER_CLASS)
  })
  container.querySelectorAll(`.${MARKER_CLASS}-badge`).forEach(el => {
    el.remove()
  })
}

function applyMarkers(container: HTMLElement, lowResImages: LowResImage[]) {
  if (!LOW_RES_MARKER_ENABLED || lowResImages.length === 0) return

  ensureMarkerStyles()
  const srcSet = new Set(lowResImages.map(img => img.src))
  const dpiMap = new Map(lowResImages.map(img => [img.src, img.dpi]))

  container.querySelectorAll<HTMLImageElement>('.print-quiz-wrapper img').forEach(img => {
    if (!srcSet.has(img.src)) return

    const wrapper = img.parentElement
    if (!wrapper || wrapper.classList.contains(MARKER_CLASS)) return

    // Ensure wrapper is positioned for the badge
    const pos = getComputedStyle(wrapper).position
    if (pos === 'static') wrapper.style.position = 'relative'

    wrapper.classList.add(MARKER_CLASS)

    const badge = document.createElement('span')
    badge.className = `${MARKER_CLASS}-badge`
    badge.textContent = `${dpiMap.get(img.src)}DPI`
    wrapper.appendChild(badge)
  })
}

export function useLowResDetection(
  printRef: React.RefObject<HTMLDivElement | null>,
  quizCount: number,
  isMeasuring: boolean
): LowResImage[] {
  const [lowResImages, setLowResImages] = useState<LowResImage[]>([])
  const prevContainerRef = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!printRef.current || quizCount === 0 || isMeasuring) {
      if (prevContainerRef.current) clearMarkers(prevContainerRef.current)
      setLowResImages([])
      return
    }

    // Delay to let rendering settle
    const timer = setTimeout(() => {
      if (!printRef.current) return

      // Clear previous markers
      clearMarkers(printRef.current)

      const images = printRef.current.querySelectorAll<HTMLImageElement>('.print-quiz-wrapper img')
      const results: LowResImage[] = []

      images.forEach(img => {
        if (!img.naturalWidth || !img.naturalHeight) return
        const rect = img.getBoundingClientRect()
        if (rect.width === 0 || rect.height === 0) return

        // Skip small coloring quiz icons (coloringOff images: 48x48)
        if (rect.width <= 56 && rect.height <= 56) return

        // Calculate effective DPI
        // Screen is 96 DPI, print needs 300 DPI
        // If natural size / display size < 300/96 ~ 3.125, it's low-res for print
        const dpiX = (img.naturalWidth / rect.width) * 96
        const dpiY = (img.naturalHeight / rect.height) * 96
        const effectiveDpi = Math.min(dpiX, dpiY)

        if (effectiveDpi < 300) {
          results.push({
            src: img.src,
            naturalWidth: img.naturalWidth,
            naturalHeight: img.naturalHeight,
            displayWidth: Math.round(rect.width),
            displayHeight: Math.round(rect.height),
            dpi: Math.round(effectiveDpi),
          })
        }
      })

      setLowResImages(results)

      // Apply visual markers
      if (LOW_RES_MARKER_ENABLED) {
        applyMarkers(printRef.current, results)
        prevContainerRef.current = printRef.current
      }
    }, 1000)

    return () => clearTimeout(timer)
  }, [printRef, quizCount, isMeasuring])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (prevContainerRef.current) clearMarkers(prevContainerRef.current)
    }
  }, [])

  return lowResImages
}
